import { showConfirm } from "@/core/popup";
import { useStepDisplay } from "@/core/composables/useStepDisplay";

export type Usage =
  | {
      type: "customField";
      step: number;
      stepNumber?: number;
      parentId?: string;
    }
  | { type: "filter"; step: number; stepNumber?: number; parentId?: string }
  | {
      type: "routerFilter";
      step: number;
      stepNumber?: number;
      route: number;
      parentId: string;
    }
  | {
      type: "action";
      step: number;
      stepNumber?: number;
      parentId: string;
      actionKey: string;
    };

function tryParseAndCollect(
  jsonStr: string,
  collect: (u: Usage) => void,
  usageBase: Usage,
  key: string
) {
  try {
    const arr = JSON.parse(jsonStr);
    if (Array.isArray(arr)) {
      arr.forEach((obj: any) => {
        if (
          obj.type === "user-defined-variable" &&
          obj.name.includes(`#${key}`)
        ) {
          collect(usageBase as Usage);
        }
      });
    }
  } catch {
    // invalid JSON → skip
  }
}

function deepScanStrings(
  obj: any,
  collect: (u: Usage) => void,
  usageBase: Usage,
  key: string
) {
  if (typeof obj === "string") {
    tryParseAndCollect(obj, collect, usageBase, key);
  } else if (Array.isArray(obj)) {
    obj.forEach((item) => deepScanStrings(item, collect, usageBase, key));
  } else if (obj && typeof obj === "object") {
    Object.values(obj).forEach((v) =>
      deepScanStrings(v, collect, usageBase, key)
    );
  }
}

function findConditionNameById(
  stepsList: any[],
  parentId: string
): string | null {
  let found: string | null = null;
  stepsList.forEach((step) => {
    if (step.type === "ROUTER" && Array.isArray(step.routes)) {
      step.routes.forEach((route) => {
        if (route.id === parentId) {
          found = `Condition "${route.name}"`;
        } else {
          const nested = findConditionNameById(route.steps || [], parentId);
          if (nested && !found) {
            found = nested;
          }
        }
      });
    }
  });
  return found;
}

export function findUdvUsages(key: string, stepArray: any[]): Usage[] {
  const usages: Usage[] = [];
  const collect = (u: Usage & { parentId?: string }) => {
    if (
      !usages.some(
        (existing) =>
          // eslint-disable-next-line
          existing.type === u.type &&
          existing.step === u.step &&
          (existing as any).parentId === u.parentId &&
          (u.type !== "action" ||
            (existing as any).actionKey === (u as any).actionKey) &&
          (u.type !== "routerFilter" ||
            (existing as any).route === (u as any).route)
      )
    ) {
      usages.push(u);
    }
  };

  function visitSteps(list: any[]) {
    list.forEach((step) => {
      const stepIx = step.ix;
      const { stepNumber } = step;
      const { parentId } = step;

      if (
        step.udvChangeMapBefore?.[key] !== undefined ||
        step.udvChangeMapAfter?.[key] !== undefined
      ) {
        collect({ type: "customField", step: stepIx, stepNumber, parentId });
      }

      (step.filter || []).forEach((filt: any) => {
        tryParseAndCollect(
          filt.firstInput,
          collect,
          { type: "filter", step: stepIx, stepNumber, parentId },
          key
        );
        tryParseAndCollect(
          filt.secondInput,
          collect,
          { type: "filter", step: stepIx, stepNumber, parentId },
          key
        );
      });

      if (step.actions) {
        Object.entries(step.actions).forEach(([actionKey, action]) => {
          deepScanStrings(
            action,
            collect,
            {
              type: "action",
              step: stepIx,
              stepNumber,
              parentId,
              actionKey,
            },
            key
          );
        });
      }

      if (step.type === "ROUTER" && Array.isArray(step.routes)) {
        step.routes.forEach((route) => {
          (route.filter || []).forEach((filt: any) => {
            const usageBase: Usage = {
              type: "routerFilter",
              step: stepIx,
              stepNumber,
              route: route.iy - 1,
              parentId: route.id,
            };
            tryParseAndCollect(filt.firstInput, collect, usageBase, key);
            tryParseAndCollect(filt.secondInput, collect, usageBase, key);
          });
          visitSteps(route.steps || []);
        });
      }
    });
  }

  visitSteps(stepArray);
  return usages;
}

export async function confirmAndRemoveUdv(
  key: string,
  index: number,
  userDefinedVariables: any[],
  steps: any[],
  t: (path: string, params?: Record<string, any>) => string
): Promise<boolean> {
  const usages = findUdvUsages(key, steps);
  const { getStepDisplayNumber } = useStepDisplay();

  if (usages.length > 0) {
    const header = t("Flow.UdvInUseHeader", { key });
    const bullets = usages
      .map((u) => {
        const stepNum = getStepDisplayNumber(u.step, u.stepNumber);
        const condition = findConditionNameById(steps, (u as any)?.parentId);
        const prefix = condition ? `${condition} - ` : "";
        switch (u.type) {
          case "customField":
            return `• ${prefix}${t("Flow.UdvInUseCustomField", {
              step: stepNum,
            })}`;
          case "filter":
            return `• ${prefix}${t("Flow.UdvInUseFilter", { step: stepNum })}`;
          case "routerFilter":
            return `• ${t("Flow.UdvInUseRouterFilter", {
              step: stepNum,
              route: (u as any).route + 1,
            })}`;
          case "action":
            return `• ${prefix}${t("Flow.UdvInUseAction", {
              step: stepNum,
              action: (u as any).actionKey,
            })}`;
          default:
            return "";
        }
      })
      .filter(Boolean)
      .join("<br>");

    const footer = t("Flow.UdvCannotDeleteEnding", {
      key,
      steps: Array.from(
        new Set(usages.map((u) => getStepDisplayNumber(u.step, u.stepNumber)))
      ).join(", "),
    });
    const msg = `${header}<br>${bullets}<br>${footer}`;

    await showConfirm("warning", t("Flow.DeleteUdv"), msg);
    return false;
  }

  const ok = await showConfirm(
    "warning",
    t("Flow.DeleteUdv"),
    t("Flow.DeleteUdvConfirm", { key })
  );
  if (ok) {
    userDefinedVariables.splice(index, 1);
    return true;
  }
  return false;
}
